import {
  compileSkinFallbackPlan,
  compileSkinVisualPlan,
  SKIN_RUNTIME_MANIFEST_FILE,
  type SkinLodLevel,
  type SkinModality,
  type SkinRuntimeEntry,
  type SkinRuntimeManifest,
  type SkinVisualPlan,
  skinRuntimeManifestSchema,
  skinTextureKey,
} from '@kenocyber/shared';
import { DEV_TOOLS_ALLOWED } from '../dev-tools';

/**
 * The skins this build is allowed to draw (ADR 0041).
 *
 * ## One source, validated at the boundary
 *
 * The sidecar is produced by the asset pipeline from `assets-source/skins`,
 * which is where every number is authored. This fetches it and re-validates it
 * with the shared schema, so a stale or hand-edited file fails loudly instead
 * of rendering something nobody checked.
 *
 * ## Missing is a normal answer
 *
 * A build with no finished skins emits an empty sidecar, and a tree that was
 * never prepared has none at all. Both mean "no skins", and neither is an
 * error: the game has to start.
 *
 * ## Draft content is gated twice
 *
 * The pipeline only writes unfinished entries when a build explicitly asks, and
 * production never does. On top of that, {@link listSelectableSkins} refuses to
 * enumerate anything but `ready` unless the build constant says dev tooling is
 * compiled in - so even a sidecar that somehow carried drafts could not be
 * browsed from a production bundle.
 */

const MANIFEST_URL = `/game-assets/${SKIN_RUNTIME_MANIFEST_FILE}`;

/** The parsed sidecar, or null when this build ships no skins at all. */
export async function fetchSkinManifest(): Promise<SkinRuntimeManifest | null> {
  let response: Response;
  try {
    response = await fetch(MANIFEST_URL);
  } catch {
    // Offline, or a tree that was never prepared. Not an error: no skins.
    return null;
  }
  if (!response.ok) {
    return null;
  }
  return skinRuntimeManifestSchema.parse(await response.json());
}

/**
 * The entries a caller may choose from.
 *
 * `ready` always. Everything else ONLY when dev tooling is compiled in, which
 * is a build constant Vite replaces - so the branch is removed entirely from a
 * production bundle rather than being a check somebody could flip.
 */
export function listSelectableSkins(
  manifest: SkinRuntimeManifest | null,
): readonly SkinRuntimeEntry[] {
  if (!manifest) {
    return [];
  }
  if (DEV_TOOLS_ALLOWED) {
    return manifest.entries;
  }
  return manifest.entries.filter((entry) => entry.status === 'ready');
}

export function findSkinEntry(
  manifest: SkinRuntimeManifest | null,
  definitionId: string,
): SkinRuntimeEntry | null {
  return listSelectableSkins(manifest).find((entry) => entry.definitionId === definitionId) ?? null;
}

/** The absolute URL of one skin's PNG, from the manifest's own base. */
export function skinAssetUrl(manifest: SkinRuntimeManifest, entry: SkinRuntimeEntry): string {
  return `${manifest.baseUrl}${entry.file}`;
}

export { skinTextureKey };

/**
 * The plan for one entry, in one mode, at one level of detail.
 *
 * A thin wrapper over the shared compiler so callers do not have to unpack the
 * entry themselves - and so "which asset and which descriptor go together" is
 * answered in one place.
 */
export function planForSkin(
  entry: SkinRuntimeEntry,
  modality: SkinModality,
  lod: SkinLodLevel,
): SkinVisualPlan {
  return compileSkinVisualPlan({
    asset: entry.asset,
    vfx: entry.vfx,
    modality,
    lod,
    // Straight through from the sidecar: the mask the pipeline read once.
    bodyPoints: entry.bodyPoints,
  });
}

/** The plan a renderer falls back to when a mode cannot be resolved. */
export function fallbackPlanForSkin(entry: SkinRuntimeEntry): SkinVisualPlan {
  return compileSkinFallbackPlan({ asset: entry.asset, vfx: entry.vfx });
}
